import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';

import BackButton from '../../custom/BackButton';
import { ExportDimension, IBlobFile, IExport, IQueue } from '../../../globals/interfaces';
import ActionIcon from '../../custom/ActionIcon';
import VirtualTable from '../../custom/VirtualTable';
import CreateExportDeliverySheet from './CreateExportDeliverySheet';
import apiClient, { apiClientEosNative } from '../../../apiClient';
import { renderToString } from 'react-dom/server';
import useUser from '../../../customHooks/useUser';
import useSWR from 'swr';
import CustomLightBox, { RenderLightBoxImage } from '../../LightBox';
import useLightBox from '../../LightBox/useLightBox';
import useLoading from '../../../customHooks/useLoading';
import { formatDecimal, formatMawb, notify } from '../../../utils';
import _ from 'lodash';

export interface DimTotal {
    name: string;
    value: string;
}

const defaultData = {
    queueRecord: undefined,
    exportRecord: undefined,
    dimensions: [],
    stationInfo: undefined
}

const fetcher = async (key): Promise<{
    queueRecord: IQueue;
    exportRecord: IExport;
    dimensions: Array<ExportDimension>;
    stationInfo: any;
}> => {
    try {
        const res = await apiClient.get(key);
        return res.data;
    } catch (err) {
        alert(err);
        return defaultData;
    }
}

export default function ModalExportDimensions({
    modal,
    setModal,
    selectedRecord
}: {
    modal: boolean;
    setModal: React.Dispatch<React.SetStateAction<boolean>>;
    selectedRecord: IQueue | undefined;
}) {

    const { user } = useUser();

    const { data } = useSWR(selectedRecord && selectedRecord.s_mawb_id ? `/export-details/${selectedRecord.s_mawb_id}` : null,
        fetcher,
        {
            fallbackData: defaultData
        }
    );
    const { stationInfo, dimensions, queueRecord, exportRecord } = data;

    const { weight: totalWeight, volume: totalVolume, length: totalLength, height: totalHeight, width: totalWidth } = dimensions.reduce((total, current) => {
        const { weight, volume, length, height, width } = current;
        total.weight += weight;
        total.volume += volume;
        total.length += length;
        total.height += height;
        total.width += width;
        return total;
    }, { weight: 0, volume: 0, length: 0, height: 0, width: 0 });

    const chargeableWeight = dimensions.reduce((total, { length, width, height }) => {
        const volumetricWeight = (length * width * height) / 366;
        return total + volumetricWeight;
    }, 0);

    const deltaMeanWeight = ((chargeableWeight - totalWeight) / chargeableWeight) * 100;

    const totals: Array<DimTotal> = [{
        name: 'Pieces',
        value: String(_.get(exportRecord, 'i_pieces', 0))
    }, {
        name: 'Volume',
        value: `${formatDecimal(totalVolume)} in³`
    }, {
        name: 'Weight',
        value: `${formatDecimal(totalWeight)} kg`
    }, {
        name: 'Chargeable Weight',
        value: `${formatDecimal(chargeableWeight)} kg`
    }, {
        name: 'Delta Mean Weight',
        value: `${deltaMeanWeight ? formatDecimal(deltaMeanWeight) : ''}%`
    }];


    const { lightBoxOpen, setLightBoxOpen, images, setImages } = useLightBox();

    const generateExportSheet = async () => {
        try {
            const deliverySheetPrint = renderToString(
                <CreateExportDeliverySheet
                    user={user}
                    stationInfo={stationInfo}
                    queueRecord={queueRecord}
                    exportRecord={exportRecord}
                    dimensions={dimensions}
                    totals={totals}
                />
            );

            const myWindow = window.open("", "MsgWindow", "width=1920,height=1080");
            myWindow.document.write(deliverySheetPrint);
        } catch (err) {
            alert(err);
        }
    }

    const viewImages = (exportDimension: ExportDimension) => {
        const { created, createdBy, pieces, length, height, width, images } = exportDimension;

        if (!images || !images.length) {
            notify('No images found', 'warn');
            return;
        }

        setImages(images.filter(img => Boolean(img)).map((item: IBlobFile) => ({ 
            url: item.accessLink,
            title: item.s_file_name,
            comments: `${pieces} pieces. ${length}x${width}x${height}.`,
            createdAt: created,
            createdBy
        })));

        setLightBoxOpen(true);
    }

    const toggle = () => setModal(!modal);

    if (!queueRecord) return null;

    return (
        <CustomModal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <FooterHeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2 pt-2'}>Weight and Dims for {formatMawb(queueRecord.s_mawb)}</h4>
                </FooterHeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FlexContainer>
                    <FlexContainer>
                        {
                            totals.map((item, i) => (
                                <Total 
                                    name={item.name}
                                    value={item.value}
                                    key={i}
                                />
                            ))
                        }
                    </FlexContainer>
                    <ActionIcon
                        type={'print'}
                        onClick={() => generateExportSheet()}
                    />
                </FlexContainer>
                <VirtualTable<ExportDimension>
                    data={dimensions}
                    mapping={[{
                        name: 'Pieces',
                        value: 'pieces',
                        number: true,
                        customWidth: 70
                    }, {
                        name: 'Length',
                        value: 'length',
                        number: true,
                        decimal: true,
                        customWidth: 70
                    }, {
                        name: 'Width',
                        value: 'width',
                        number: true,
                        decimal: true,
                        customWidth: 70
                    }, {
                        name: 'Height',
                        value: 'height',
                        number: true,
                        decimal: true,
                        customWidth: 70
                    }, {
                        name: 'Volume',
                        value: 'volume',
                        decimal: true,
                        number: true,
                        customWidth: 70
                    }, {
                        name: 'Weight',
                        value: 'weight',
                        decimal: true,
                        number: true,
                        customWidth: 70
                    }, {
                        name: 'Modified',
                        value: 'modified',
                        datetime: true,
                        utc: true
                    }, {
                        name: 'Modified by',
                        value: 'modifiedBy',
                        email: true
                    }, {
                        name: '',
                        value: 'fas fa-image',
                        icon: true,
                        function: (item: ExportDimension) => viewImages(item),
                        showCondition: (item: ExportDimension) => Boolean(item.scanner)
                    }]}
                    numRows={10}
                />
            </ModalBody>
            <CustomLightBox 
                images={images}
                lightBoxOpen={lightBoxOpen}
                setLightBoxOpen={setLightBoxOpen}
            />
        </CustomModal>
    );
}

const Total = ({
    value,
    name
}: {
    value: string;
    name: string;
}) => {
    return (
        <TotalsContainer>
            <h6>{value}</h6>
            <h6>{name}</h6>
        </TotalsContainer>
    );
}

const CustomModal = styled(Modal)`
    max-width: 900px;
`;

const FooterHeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
`;

const TotalsContainer = styled.div`
    height: 100px;
    width: 150px;
    background-color: #6fb327;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: white;
    border-radius: 20px;
    margin-bottom: 10px;
`;
